import { useEffect, useState } from "react";
import { OAuthError, postData } from "../backend";
import { useModal } from "../contexts";
import useUser from "../hooks/useUser";
import OAuthModal from "./OAuthModal";
import SettingsModalGlide from "./SettingsModalGlide";
import SettingsModalXero from "./SettingsModalXero";
import SettingsModalHubspot from "./SettingsModalHubspot";
import SettingsModalMicrosoft from "./SettingsModalMicrosoft";
import SettingsModalZapier from "./SettingsModalZapier";
import OAuthModalHubspot from "./OAuthModalHubspot";
import LoadingModal from "./LoadingModal";
import SettingsModalCopper from "./SettingsModalCopper";
import OAuthModalCopper from "./OAuthModalCopper";
import SettingsPermissionsItem from "./SettingsPermissionsItem";
import useScopes from "../hooks/useScopes";
import SettingsPermissionsGoogle from "./SettingsPermissionsGoogle";

const integrationsInitialState = {
  hubspot: false,
  copper: false,
  xero: false,
  microsoft: false,
  zapier: false,
  glide: false
}

function SettingsPermissions() {
  const { openModal, closeModal } = useModal();
  const [loading, setLoading] = useState({ ...integrationsInitialState });
  const [credentialsStatus, setCredentialsStatus] = useState({ ...integrationsInitialState });

  const { scopes, refreshScopes, isLoading: loadingGoogle, isValidating: validatingGoogle } = useScopes();
  const { user } = useUser();
  const { refreshUser } = useUser();

  const integrationsData = [
    {
      title: 'HubSpot',
      description: 'Portant will have access to read your Deal, Company, and Contact data.',
      connectClickAction: openHubspotModal,
      manageClickAction() { openModal(<SettingsModalHubspot />) }
    },
    {
      title: 'Copper',
      description: 'Portant will have access to read your Copper CRM opportunities data.',
      connectClickAction: openCopperModal,
      manageClickAction() { openModal(<SettingsModalCopper />) }
    },
    {
      title: 'Xero',
      description: 'Portant will have access to read your Invoice data.',
      connectClickAction() { openModal(<SettingsModalXero />) },
      manageClickAction() { openModal(<SettingsModalXero />) }
    },
    {
      title: 'Microsoft',
      description: 'Portant will have access to your OneDrive for Business and Outlook.',
      connectClickAction: connectToMicrosoft,
      manageClickAction() { openModal(<SettingsModalMicrosoft />) },
      isBeta: true
    },
    {
      title: 'Zapier',
      description: 'Zapier will be able to access your Portant workflows.',
      connectClickAction() { openModal(<SettingsModalZapier />) },
      manageClickAction() { openModal(<SettingsModalZapier />) }
    },
    {
      title: 'Glide',
      description: 'Portant will have access to write data back to your nominated table in Glide.',
      connectClickAction() { openModal(<SettingsModalGlide />) },
      manageClickAction() { openModal(<SettingsModalGlide />) }
    }
  ]

  useEffect(() => {
    handleAuthoriseIntegration('hubspot');
  }, [user?.hubspotCredentials])

  useEffect(() => {
    handleAuthoriseIntegration('xero');
  }, [user?.xeroCredentials])

  useEffect(() => {
    handleAuthoriseIntegration('microsoft')
  }, [user?.microsoftCredentials]);

  useEffect(() => {
    handleAuthoriseIntegration('copper');
  }, [user?.copperCredentials]);

  function handleAuthoriseScope(scope: string) {
    postData("/auth/access-token/", { scopes: [scope] })
      .then(() => refreshScopes())
      .catch(error => {
        if (error instanceof OAuthError)
          openModal(<OAuthModal oauthUrl={error.oauthUrl} onClose={refreshUser} />);
        else
          console.error(error)
      });
  }

  function handleAuthoriseIntegration(name: string) {
    setLoading(prev => ({ ...prev, [name]: true }));
    postData(`/${name}/access-token/`, {})
      .then(() => setCredentialsStatus(prev => ({ ...prev, [name]: true })))
      .catch(() => setCredentialsStatus(prev => ({ ...prev, [name]: false })))
      .finally(() => setLoading(prev => ({ ...prev, [name]: false })))
  }

  async function openHubspotModal() {
    openModal(<LoadingModal />)

    const { url } = await postData("/hubspot/install/");
    openModal(<OAuthModalHubspot oauthUrl={url} />);
  }

  async function openCopperModal() {
    openModal(<LoadingModal />)

    setLoading(prev => ({ ...prev, copper: true }))
    postData("/copper/access-token/")
      .then(() => { setCredentialsStatus(prev => ({ ...prev, copper: true })); closeModal() })
      .catch(error => {
        if (error instanceof OAuthError)
          openModal(<OAuthModalCopper oauthUrl={error.oauthUrl} onClose={refreshUser} />);
        else
          console.error(error)
      })
      .finally(() => setLoading(prev => ({ ...prev, copper: false })));
  }

  async function connectToMicrosoft() {
    const { url } = await postData("/microsoft/install/");
    window.open(url, "_blank");
  }

  return (
    <div className="flex flex-col p-8 overflow-y-auto">
      <div className="font-gilroy font-semibold text-lg">
        Permissions
      </div>
      <div className="text-gray-400 mb-8">
        Manage services of which you have provided Portant access to
      </div>
      <div className="flex flex-col gap-8 w-full">
        <div className="font-gilroy font-semibold -mb-6">Gmail</div>
        <SettingsPermissionsGoogle
          scope="https://www.googleapis.com/auth/gmail.send"
          isLoading={loadingGoogle || validatingGoogle}
          scopes={scopes}
          onClickConnect={handleAuthoriseScope}
        />
        <div className="font-gilroy font-semibold -mb-6">Google Drive</div>
        <SettingsPermissionsGoogle
          scope="https://www.googleapis.com/auth/drive.file"
          isLoading={loadingGoogle || validatingGoogle}
          scopes={scopes}
          onClickConnect={handleAuthoriseScope}
        />
        <SettingsPermissionsGoogle
          scope="https://www.googleapis.com/auth/drive"
          isLoading={loadingGoogle || validatingGoogle}
          scopes={scopes}
          onClickConnect={handleAuthoriseScope}
        />

        <div className="font-gilroy font-semibold -mb-6">Integrations</div>
        {
          integrationsData.map(cred => (
            <SettingsPermissionsItem
              title={cred.title}
              description={cred.description}
              iconName={cred.title.toUpperCase()}
              onConnectClick={cred.connectClickAction}
              onManageClick={cred.manageClickAction}
              hasValidCredentials={credentialsStatus[cred.title.toLowerCase() as keyof typeof credentialsStatus]}
              isLoading={loading[cred.title.toLowerCase() as keyof typeof loading]}
              isBeta={cred.isBeta}
            />
          ))
        }
      </div>
    </div>
  );
}

export default SettingsPermissions;
